import { CacheError } from '../client/errors.js'

const WORKER_RESPONSE_TIMEOUT_MS = 1_000

interface PendingRequest {
  resolve: (value: unknown) => void
  reject: (reason: unknown) => void
}

/**
 * Abstraction du canal de communication avec le SharedWorker de cache.
 *
 * - Utilise un SharedWorker si le navigateur le supporte
 * - Fallback : Map in-memory (données non persistantes, perdues au reload)
 *
 * Chaque appel retourne une Promise, résolue/rejetée via la réponse du worker.
 */
export class WorkerBridge {
  private worker: SharedWorker | null = null
  private port: MessagePort | null = null
  private readonly pending = new Map<string, PendingRequest>()
  private requestCounter = 0
  private readonly fallback = new Map<string, { data: unknown; expiresAt: number }>()
  private usingFallback: boolean

  constructor() {
    this.usingFallback = !this.tryStartWorker()
  }

  private tryStartWorker(): boolean {
    if (typeof SharedWorker === 'undefined') return false

    try {
      const worker = new SharedWorker(
        new URL('./cache.worker.ts', import.meta.url),
        { type: 'module', name: 'altazion-cache-worker' }
      )
      this.worker = worker
      this.port = worker.port
      this.worker.addEventListener('error', this.handleWorkerError)
      this.port.addEventListener('message', this.handleMessage)
      this.port.addEventListener('messageerror', this.handlePortMessageError)
      this.port.start()
      return true
    } catch {
      this.activateFallback()
      return false
    }
  }

  private readonly handleWorkerError = (event: ErrorEvent): void => {
    this.activateFallback(event.error ?? event.message)
  }

  private readonly handlePortMessageError = (event: MessageEvent): void => {
    this.activateFallback(event)
  }

  private readonly handleMessage = (event: MessageEvent<{ id: string; result?: unknown; error?: string }>): void => {
    const { id, result, error } = event.data
    const pending = this.pending.get(id)
    if (!pending) return
    this.pending.delete(id)

    if (error !== undefined) {
      pending.reject(new CacheError(error))
    } else {
      pending.resolve(result)
    }
  }

  private nextId(): string {
    return String(++this.requestCounter)
  }

  private send(message: Record<string, unknown>): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (this.usingFallback || !this.port) {
        reject(new CacheError('Worker non initialisé'))
        return
      }

      const id = this.nextId()
      const timeoutHandle = globalThis.setTimeout(() => {
        const pending = this.pending.get(id)
        if (!pending) {
          return
        }

        this.pending.delete(id)
        const error = new CacheError('Le worker de cache ne repond pas')
        this.activateFallback(error)
        reject(error)
      }, WORKER_RESPONSE_TIMEOUT_MS)

      this.pending.set(id, {
        resolve: (value) => {
          globalThis.clearTimeout(timeoutHandle)
          resolve(value)
        },
        reject: (reason) => {
          globalThis.clearTimeout(timeoutHandle)
          reject(reason)
        },
      })

      try {
        this.port.postMessage({ ...message, id })
      } catch (error) {
        globalThis.clearTimeout(timeoutHandle)
        this.pending.delete(id)
        this.activateFallback(error)
        reject(new CacheError('Impossible de communiquer avec le worker de cache', error))
      }
    })
  }

  async get<T>(key: string): Promise<T | null> {
    if (this.usingFallback) {
      return this.getFromFallback<T>(key)
    }

    try {
      return await this.send({ type: 'GET', key }) as T | null
    } catch {
      this.activateFallback()
      return this.getFromFallback<T>(key)
    }
  }

  async set(key: string, data: unknown, ttlMs: number): Promise<void> {
    if (this.usingFallback) {
      this.setInFallback(key, data, ttlMs)
      return
    }

    try {
      await this.send({ type: 'SET', key, data, ttl: ttlMs })
    } catch {
      this.activateFallback()
      this.setInFallback(key, data, ttlMs)
    }
  }

  async invalidate(pattern: string): Promise<void> {
    if (this.usingFallback) {
      this.invalidateFallback(pattern)
      return
    }

    try {
      await this.send({ type: 'INVALIDATE', pattern })
    } catch {
      this.activateFallback()
      this.invalidateFallback(pattern)
    }
  }

  async clearAll(): Promise<void> {
    if (this.usingFallback) {
      this.fallback.clear()
      return
    }

    try {
      await this.send({ type: 'CLEAR_ALL' })
    } catch {
      this.activateFallback()
      this.fallback.clear()
    }
  }

  private activateFallback(cause?: unknown): void {
    if (this.usingFallback) {
      return
    }

    this.usingFallback = true
    this.worker?.removeEventListener('error', this.handleWorkerError)
    this.worker = null

    if (this.port) {
      this.port.removeEventListener('message', this.handleMessage)
      this.port.removeEventListener('messageerror', this.handlePortMessageError)
      this.port.close()
      this.port = null
    }

    for (const pending of this.pending.values()) {
      pending.reject(new CacheError('Worker de cache indisponible', cause))
    }

    this.pending.clear()
  }

  private getFromFallback<T>(key: string): T | null {
    const entry = this.fallback.get(key)
    if (!entry || Date.now() > entry.expiresAt) return null
    return entry.data as T
  }

  private setInFallback(key: string, data: unknown, ttlMs: number): void {
    this.fallback.set(key, { data, expiresAt: Date.now() + ttlMs })
  }

  private invalidateFallback(pattern: string): void {
    const regex = new RegExp(pattern)
    for (const key of this.fallback.keys()) {
      if (regex.test(key)) this.fallback.delete(key)
    }
  }

  dispose(): void {
    this.activateFallback()
    this.pending.clear()
  }
}
